import config from "@/config";
import { HttpStatusCodes } from "@/utils/HttpUtils";
import { Locales } from "@/utils/LocaleUtils";
import { useStorage, RemovableRef } from "@vueuse/core";
import {
  RateLimitNotifier,
  RATE_LIMITED_ERROR_FLAG,
  extractRetryAfterSeconds,
  isRateLimitedStatus,
} from "@/composable/http/rateLimitHandler";

export const LOCAL_STORAGE_LANG = "lang";
export const LOCAL_STORAGE_AUTHENTICATED_USER = "authenticatedUser";
export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";
export type SerializableBody = object;
export type UploadBody = Record<string, unknown> & {
  file?: File;
};

export type UploadProgressEvent = {
  loaded: number;
  total: number;
  percent: number;
};

export type UploadCompleteEvent = {
  fileName: string;
  size: number;
  mimeType?: string;
};

export interface FetcherSessionTracker {
  updateLastCallTime: () => void;
}

export interface FetcherLocaleProvider {
  getUserPrefLocale: () => string;
}

export interface FetcherCredentialsHandler {
  notifyCredentialsLost: () => void;
}

export interface FetcherDisconnectedDispatcher {
  dispatchDisconnected: () => void;
}

export interface FetcherAuthStore {
  getToken: () => string | null;
  setToken: (token: string | null) => void;
}

export interface FetcherDependencies {
  sessionTracker?: FetcherSessionTracker | null;
  localeProvider?: FetcherLocaleProvider | null;
  credentialsHandler?: FetcherCredentialsHandler | null;
  disconnectedDispatcher?: FetcherDisconnectedDispatcher | null;
  authStore?: FetcherAuthStore | null;
  rateLimitNotifier?: RateLimitNotifier | null;
}

export class Fetcher {
  static AUTHORIZATION: RemovableRef<string | null> = useStorage("bearerToken", null, sessionStorage);

  static #dependencies: FetcherDependencies = {};
  static TIME_OUT_ON_CHUNK: string;

  static getCsrfToken(): string | null {
    const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }

  static configure(dependencies: FetcherDependencies): void {
    Fetcher.#dependencies = {
      ...Fetcher.#dependencies,
      ...dependencies,
    };
  }

  static resetDependencies(): void {
    Fetcher.#dependencies = {};
  }

  protected get _sessionService(): FetcherSessionTracker | null {
    return Fetcher.#dependencies.sessionTracker ?? null;
  }

  protected get localeProvider(): FetcherLocaleProvider | null {
    return Fetcher.#dependencies.localeProvider ?? null;
  }

  protected get credentialsHandler(): FetcherCredentialsHandler | null {
    return Fetcher.#dependencies.credentialsHandler ?? null;
  }

  protected get disconnectedDispatcher(): FetcherDisconnectedDispatcher | null {
    return Fetcher.#dependencies.disconnectedDispatcher ?? null;
  }

  protected get authStore(): FetcherAuthStore | null {
    return Fetcher.#dependencies.authStore ?? null;
  }

  protected get rateLimitNotifier(): RateLimitNotifier | null {
    return Fetcher.#dependencies.rateLimitNotifier ?? null;
  }

  /**
   * Signale à l'utilisateur ( via le notifier injecté ) que la requête
   * a été rejetée pour rate-limit . Idempotent : sans notifier , no-op
   * silencieux . Retourne {@code true} si la réponse était bien un 429 ,
   * pour permettre aux call-sites d'aiguiller le flow ( ex retourner
   * tôt sans logger une erreur générique ) .
   */
  protected maybeNotifyRateLimit(response: Response | null | undefined): boolean {
    if (!response || !isRateLimitedStatus(response.status)) return false;
    this.rateLimitNotifier?.notifyRateLimited(
      extractRetryAfterSeconds(response.headers),
    );
    return true;
  }

  protected getAuthorizationToken(): string | null {
    return this.authStore?.getToken() ?? Fetcher.AUTHORIZATION?.value ?? null;
  }

  protected setAuthorizationToken(token: string | null): void {
    if (this.authStore) {
      this.authStore.setToken(token);
      return;
    }
    Fetcher.AUTHORIZATION.value = token;
  }

  protected dispatchDisconnected(): void {
    if (this.disconnectedDispatcher) {
      this.disconnectedDispatcher.dispatchDisconnected();
      return;
    }
    document.dispatchEvent(new CustomEvent("disconnected"));
  }

  protected defaultNotifyCredentialsLost(): void {
    localStorage.removeItem(LOCAL_STORAGE_AUTHENTICATED_USER);
  }

  // #470 - Centralise la détection d'une session expirée / invalide.
  // Depuis le fix backend #64, l'expiration ou l'invalidité d'un JWT
  // est reportée par un 401 avec un body JSON stable
  // { code: "TOKEN_EXPIRED" | "TOKEN_INVALID", message }. Plus besoin
  // de matcher la chaîne "expired JWT" dans un 500.
  protected isAuthError(response: Response): boolean {
    return response.status === HttpStatusCodes.UNAUTHORIZED;
  }

  updateLastCallTime = (response: Response): Response => {
    if (response.ok) {
      this._sessionService?.updateLastCallTime();
    }
    return response;
  };

  xhrPost(
    url: string,
    data: UploadBody,
    withFormData: boolean,
    additionalHeaders: Record<string, string> = {},
    onUploadProgress?: (e: UploadProgressEvent) => void,
    onUploadComplete?: (e: UploadCompleteEvent) => void,
  ): Promise<Response> {
    return new Promise<Response>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const fullUrl = `${config.API_URL}${url}`;

      let body: FormData | string;
      const headers: Record<string, string> = {
        "Accept-Language": this.getUserPrefLocale(),
        "Authorization": `Bearer ${this.getAuthorizationToken()}`,
        ...additionalHeaders,
      };

      if (withFormData) {
        body = this.convertToFormData(data);
        delete headers["Content-Type"];
      }
      else {
        body = JSON.stringify(data);
        headers["Content-Type"] = "application/json;charset=UTF-8";
      }

      xhr.open("POST", fullUrl);
      xhr.withCredentials = true;

      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.upload.onprogress = (e: ProgressEvent<EventTarget>) => {
        try {
          if (!onUploadComplete) {
            console.error("vous devriez définir onUploadComplete");
            return;
          }
          if (!onUploadProgress) {
            console.error("vous devriez définir onUploadProgress");
            return;
          }
          if (e.lengthComputable && e.loaded === e.total && onUploadComplete) {
            onUploadComplete({
              fileName: (data.file?.name as string) || "file",
              size: e.total,
              mimeType: data.file?.type,
            });
          }
          else if (onUploadProgress) {
            onUploadProgress({
              loaded: e.loaded,
              total: e.total,
              percent: e.lengthComputable ? e.loaded / e.total : 0,
            });
          }
          else {
            console.error("vous devez définir onUploadProgress et onUploadComplete");
          }
        }
        catch (callbackError) {
          console.error("Erreur dans les callbacks:", callbackError);
        }
      };

      xhr.onload = () => {
        const response = new Response(xhr.responseText, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: this._parseXHRHeaders(xhr),
        });
        resolve(response);
      };

      xhr.onerror = () => reject(new Error("Erreur réseau"));

      xhr.send(body);
    });
  }

  private _parseXHRHeaders(xhr: XMLHttpRequest): Headers {
    const headers = new Headers();
    const headersStr = xhr.getAllResponseHeaders();
    const lines = headersStr.trim().split(/[\r\n]+/);

    lines.forEach((line) => {
      const [name, value] = line.split(": ");
      if (name && value) {
        headers.append(name, value);
      }
    });

    return headers;
  }

  getPostFetch(
    url: string,
    data: UploadBody,
    withFormData?: boolean | null,
    additionalHeaders: Record<string, string> = {},
    onUploadProgress?: (e: UploadProgressEvent) => void,
    onUploadComplete?: (e: UploadCompleteEvent) => void,
  ): Promise<Response> {
    withFormData = withFormData == null || withFormData;
    let body: string | FormData = JSON.stringify(data);
    if (withFormData) {
      body = this.convertToFormData(data);
    }
    additionalHeaders = {
      ...additionalHeaders,
      "Authorization": `Bearer ${this.getAuthorizationToken()}`,
      "X-XSRF-TOKEN": Fetcher.getCsrfToken() ?? "",
      "Accept-Language": this.getUserPrefLocale(),
    };
    const headers = withFormData
      ? { ...additionalHeaders, "Accept-Language": this.getUserPrefLocale() }
      : {
          ...additionalHeaders,
          "Accept-Language": this.getUserPrefLocale(),
          "Content-Type": "application/json;charset=UTF-8;multipart/form-data",
        };
    const hasFile = withFormData && data.file instanceof File;

    if (hasFile && onUploadProgress) {
      return this.xhrPost(url, data, withFormData, additionalHeaders, onUploadProgress, onUploadComplete);
    }
    else {
      return fetch(`${config.API_URL}${url}`, {
        method: "POST",
        keepalive: true,
        mode: "cors",
        credentials: "include",
        body,
        headers: {
          ...headers,
          Authorization: `Bearer ${this.getAuthorizationToken()}`,
        },
      }).then(this.updateLastCallTime);
    }
  }

  async post<T = unknown>(
    url: string,
    data: SerializableBody,
    withFormData?: boolean | null,
    additionalHeaders?: Record<string, string>,
    onUploadProgress?: (e: UploadProgressEvent) => void,
    onUploadComplete?: (e: UploadCompleteEvent) => void,
  ): Promise<T> {
    const response = await this.getPostFetch(
      url,
      data as UploadBody,
      withFormData,
      {
        ...additionalHeaders,
        Authorization: `Bearer ${this.getAuthorizationToken()}`,
      },
      onUploadProgress,
      onUploadComplete,
    );

    return this._handleResponse<T>(response);
  }

  async postChunck(
    method: HttpMethod | ((message: string) => void),
    url: string,
    data: SerializableBody,
    withFormData: boolean = true,
    options?: {
      onUploadProgress?: (e: UploadProgressEvent) => void;
      onUploadComplete?: (e: UploadCompleteEvent) => void;
    },
  ) {
    return this.dochuck(
      "POST",
      method,
      url,
      data,
      withFormData,
      options?.onUploadProgress,
      options?.onUploadComplete,
    );
  }

  async dochuck(
    httpMethod: string,
    method: HttpMethod | ((message: string) => void),
    url: string,
    data: SerializableBody,
    withFormData?: boolean,
    onUploadProgress?: (e: UploadProgressEvent) => void,
    onUploadComplete?: (e: UploadCompleteEvent) => void,
  ) {
    let fetchPromise: Promise<Response>;
    if (httpMethod === "POST") {
      fetchPromise = this.getPostFetch(
        url,
        data as UploadBody,
        withFormData ?? true,
        { Accept: "application/x-ndjson" },
        onUploadProgress,
        onUploadComplete,
      );
    }
    else if (httpMethod === "GET") {
      fetchPromise = this.getGetFetch(url, data as Record<string, unknown>, {
        Accept: "application/x-ndjson, application/json, */*",
      });
    }
    else if (httpMethod === "PUT") {
      fetchPromise = this.getPutFetch(url, data, withFormData ?? true, {
        Accept: "application/x-ndjson",
      });
    }
    else {
      return Promise.reject(new Error(`Unsupported HTTP method: ${httpMethod}`));
    }

    return fetchPromise
      .then(async (response) => {
        if (!response?.ok) {
          try {
            const errorBody = await response?.json();
            console.log(errorBody);
          }
          catch {
            console.error(`Erreur ${response?.status}: Impossible de lire le corps de la réponse`);
          }
          if (response && this.isAuthError(response)) {
            this.notifyCredentialsLost();
          }
          else if (response?.status === HttpStatusCodes.FORBIDDEN) {
            console.error("Utilisateur non connecté");
            this.dispatchDisconnected();
            return;
          }
          // Quota dépôts simultanés atteint : avertir l'utilisateur via
          // toast plutôt que silent log . Sans cela , le bouton "déposer"
          // semble inerte et l'utilisateur ne comprend pas pourquoi le
          // dépôt n'a pas démarré ( cf endpoint /data/<datatype> qui
          // retourne 429 quand le user a déjà N uploads en cours ) .
          this.maybeNotifyRateLimit(response);
          return;
        }

        const reader = response.body?.getReader();
        if (!reader) return;

        const decoder = new TextDecoder();
        let result = await reader.read();
        let buffer = "";
        // Compteur de lignes NDJSON qui n'ont pas pu etre parsees - util pour
        // diagnostiquer un stream corrompu cote backend ou un bug de
        // decodage UTF-8 ( cf {@code stream: true} ci-dessous ) . Logged en
        // une seule entree console.error a la fin du stream pour eviter le
        // bruit ( un parse fail isole n'a pas a polluer la console N fois ) .
        let parseFailureCount = 0;
        const tryEmit = (line: string): void => {
          try {
            const message = JSON.parse(line);
            if (typeof method === "function") {
              method(message);
            }
          } catch {
            parseFailureCount++;
          }
        };
        while (!result.done) {
          // {@code stream: true} indispensable pour streaming NDJSON : sans
          // ce flag , si une frontiere de chunk TCP tombe au milieu d'un
          // caractere UTF-8 multi-byte ( ex 'é' = 2 bytes , noms d'apps
          // accentues "Données" , "Clés" , etc ) , le decoder substitue le
          // byte incomplet par U+FFFD ( ? ) -> JSON.parse() throw dans le
          // catch silencieux -> ligne entiere PERDUE . Si la ligne perdue
          // etait un event PROGRESS ( ex 0.85 ) ou le PROGRESS final ( 1.0 ) ,
          // la progressbar reste bloquee en plein milieu . F5 marche parce
          // que les chunks TCP retombent ailleurs par chance . Bug
          // intermittent dependant du timing reseau . Reference MDN :
          // https://developer.mozilla.org/en-US/docs/Web/API/TextDecoder/decode
          buffer += decoder.decode(result.value, { stream: true });

          let idx = buffer.indexOf("\n");
          while (idx !== -1) {
            tryEmit(buffer.substring(0, idx));
            buffer = buffer.substring(idx + 1);
            idx = buffer.indexOf("\n");
          }
          result = await reader.read();
        }
        // Flush final du decoder ( appel sans {@code stream: true} ) : si le
        // backend a tronque ses bytes en plein milieu d'un codepoint UTF-8
        // ( socket fermee mid-codepoint , extremement rare mais possible ) ,
        // {@code decoder.decode()} emet U+FFFD pour signaler l'erreur et
        // libere les bytes residuels . Sans ce flush , les derniers bytes
        // incomplets restent silencieusement coinces dans le decoder buffer .
        buffer += decoder.decode();
        if (buffer.trim()) {
          tryEmit(buffer);
        }
        if (parseFailureCount > 0) {
          // Log unique a la fin du stream pour diagnostic + tracking eventuel
          // ( un parse fail = soit un bug UTF-8 ici , soit un backend qui
          // emet du JSON malforme : les deux cas meritent une trace ) .
          console.error(`[Fetcher] ${parseFailureCount} NDJSON line(s) failed to parse for ${url}`);
        }
      })
      .catch((error) => {
        console.error("Utilisateur non connecté", error);
        this.dispatchDisconnected();
      });
  }

  async send(urlPath: string): Promise<Response | boolean> {
    const response = await fetch(urlPath.startsWith(config.API_URL) ? urlPath : `${config.API_URL}${urlPath}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${this.getAuthorizationToken()}`,
      },
    });
    if (!response.ok && this.isAuthError(response)) {
      this.notifyCredentialsLost();
    }
    return response;
  }

  getChunck(method: HttpMethod | ((message: string) => void), url: string = "json/flux", params?: Record<string, unknown>): void {
    this.dochuck("GET", method, url, params ?? {});
  }

  async put<T = unknown>(url: string, data?: SerializableBody, withFormData: boolean = true): Promise<T> {
    const response = await this.getPutFetch(url, data, withFormData);
    return this._handleResponse<T>(response);
  }

  getPutFetch(
    url: string,
    data: SerializableBody | undefined,
    withFormData: boolean,
    additionalHeaders: Record<string, string> = {},
  ): Promise<Response> {
    let body: string | FormData = JSON.stringify(data);
    const headers: Record<string, string> = {
      ...additionalHeaders,
      "Authorization": `Bearer ${this.getAuthorizationToken()}`,
      "X-XSRF-TOKEN": Fetcher.getCsrfToken() ?? "",
      "Accept-Language": this.getUserPrefLocale(),
    };

    if (withFormData) {
      body = this.convertToFormData(data as Record<string, unknown>);
    }
    else {
      headers["Content-Type"] = "application/json";
    }

    return fetch(`${config.API_URL}${url}`, {
      method: "PUT",
      mode: "cors",
      credentials: "include",
      body,
      headers,
    }).then(this.updateLastCallTime);
  }

  async get<T = unknown>(url: string, params: Record<string, unknown> = {}, isText?: boolean): Promise<T> {
    const response = await this.getGetFetch(url, params);
    return this._handleResponse<T>(response, isText);
  }

  getGetFetch(url: string, params: Record<string, unknown> = {}, additionalHeaders: Record<string, string> = {}): Promise<Response> {
    const path = new URL(url, config.API_URL);

    Object.entries(params).forEach(([name, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          path.searchParams.append(name, String(v));
        });
      }
      else {
        path.searchParams.append(name, String(value));
      }
    });

    return fetch(path, {
      method: "GET",
      mode: "cors",
      credentials: "include",
      headers: {
        ...additionalHeaders,
        "Authorization": `Bearer ${this.getAuthorizationToken()}`,
        "Accept-Language": this.getUserPrefLocale(),
      },
    }).then(this.updateLastCallTime);
  }

  getPath(url: string, params: Record<string, unknown> = {}): URL {
    const path = new URL(url, config.API_URL);

    Object.entries(params).forEach(([name, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          path.searchParams.append(name, String(v));
        });
      }
      else {
        path.searchParams.append(name, String(value));
      }
    });
    return path;
  }

  async delete<T = unknown>(url: string, data?: SerializableBody): Promise<T> {
    const formData = this.convertToFormData((data ?? {}) as Record<string, unknown>);
    const response = await fetch(`${config.API_URL}${url}`, {
      method: "DELETE",
      mode: "cors",
      credentials: "include",
      body: formData,
      headers: {
        "Authorization": `Bearer ${this.getAuthorizationToken()}`,
        "X-XSRF-TOKEN": Fetcher.getCsrfToken() ?? "",
        "Accept-Language": this.getUserPrefLocale(),
      },
    }).then(this.updateLastCallTime);

    if (response.ok) {
      return this._handleResponse<T>(response);
    }
    else {
      if (this.isAuthError(response)) {
        this.notifyCredentialsLost();
      }
      const rateLimited = this.maybeNotifyRateLimit(response);
      const isJson = response.headers.get("content-type")?.includes("application/json");
      if (isJson) {
        const data = await response.json();
        return Promise.reject({
          status: response.status,
          content: data,
          [RATE_LIMITED_ERROR_FLAG]: rateLimited,
        });
      }
      return Promise.reject({
        status: response.status,
        [RATE_LIMITED_ERROR_FLAG]: rateLimited,
      });
    }

    return Promise.reject({ status: response.status });
  }

  /**
   * GET avec revalidation conditionnelle ETag côté JS.
   *
   * <p>Si {@code cache} contient déjà une entrée pour {@code cacheKey} :
   * envoie {@code If-None-Match} au backend. Si la réponse est {@code 304} ,
   * restitue le body cached ( aucun retransfert ). Sinon , parse la nouvelle
   * réponse , extrait l'ETag et met à jour le cache.
   *
   * <p>Les erreurs HTTP suivent le même contrat que {@link #_handleResponse}
   * ( reject avec {@code httpHeaders / httpResponseCode / content} , appel à
   * {@link #notifyCredentialsLost} si 401/403 ) afin que les sites d'appel
   * gèrent les toasts d'erreur uniformément.
   *
   * @param url        Chemin relatif à {@code config.API_URL} ( pas le préfixe complet )
   * @param params     Query params ( cf. {@link #getGetFetch} )
   * @param cache      Cache LRU partagé entre appels successifs
   * @param cacheKey   Clé d'identité du couple ( endpoint , paramétrage )
   * @param onResponseHeaders Hook optionnel appele apres la reception des
   *                          headers de la reponse serveur ( pas appele en
   *                          cas de 304 servi depuis le cache JS ; dans
   *                          ce cas {@code fromClientCache=true} signale au
   *                          callsite de garder l'eventuel etat precedent ) .
   *                          Permet d'exposer des headers de diagnostic
   *                          ( ex . {@code X-Filter-Cache} ) sans casser
   *                          la signature de retour .
   */
  protected async getJsonWithEtag<T>(
    url: string,
    params: Record<string, unknown>,
    cache: import("@/services/EtagCache").EtagCache<T>,
    cacheKey: string,
    onResponseHeaders?: (headers: Headers, fromClientCache: boolean) => void,
  ): Promise<T> {
    const cached = cache.get(cacheKey);
    const headers: Record<string, string> = {};
    if (cached) headers["If-None-Match"] = cached.etag;

    const response = await this.getGetFetch(url, params, headers);
    // Notify side-channel observers ( diagnostic headers like
    // X-Filter-Cache ) BEFORE the 304-shortcut so callers always see the
    // server headers when a network round-trip occurred .
    onResponseHeaders?.(response.headers, false);

    // Refresh JWT côté client si le backend en a renvoyé un nouveau
    // ( idem _handleResponse ).
    const bearer = response.headers.get("Authorization");
    if (bearer) {
      this.setAuthorizationToken(
        bearer.startsWith("Bearer ") ? bearer.substring(7) : bearer,
      );
    }

    if (response.status === 304 && cached) {
      return cached.body;
    }

    if (!response.ok) {
      if (this.isAuthError(response)) this.notifyCredentialsLost();
      const rateLimited = this.maybeNotifyRateLimit(response);
      const errorBody = await response.json().catch(() => ({}));
      return Promise.reject({
        httpHeaders: response.headers,
        httpResponseCode: response.status,
        content: Promise.resolve(errorBody),
        [RATE_LIMITED_ERROR_FLAG]: rateLimited,
      });
    }

    // PERF audit (8/5/26) - on lit le body en text d'abord pour pouvoir
    // mesurer la taille réelle du payload ( length * 2 = approx. UTF-16
    // bytes ) avant de le passer au cache. Sans ça , EtagCache ne peut pas
    // borner la mémoire et un onglet qui visite des dizaines de
    // datatypes de gros référentiels peut grimper à plusieurs centaines de
    // MB. Le surcoût ( double allocation text + parsed ) est borné par la
    // taille de la réponse de toute façon.
    const text = await response.text();
    const body = JSON.parse(text) as T;
    const etag = response.headers.get("ETag");
    if (etag) {
      cache.set(cacheKey, etag, body, text.length * 2);
    }
    return body;
  }

  private async _handleResponse<T = unknown>(response: Response, isText?: boolean): Promise<T> {
    try {
      // Le backend peut renvoyer le JWT soit brut, soit préfixé par "Bearer ",
      // selon la convention retenue. On accepte les deux :
      //   - token brut : permet le copier-coller direct dans Swagger UI
      //     (qui ajoute automatiquement "Bearer " lors de l'envoi des requêtes)
      //   - "Bearer <token>" : convention legacy / alternative, symétrique
      //     avec le format envoyé par le front dans ses requêtes
      const bearer = response.headers.get("Authorization");
      if (bearer) {
        this.setAuthorizationToken(
          bearer.startsWith("Bearer ") ? bearer.substring(7) : bearer,
        );
      }
      const contentType = response.headers.get("content-type");
      if (contentType === "application/pdf;charset=UTF-8") {
        return response as T;
      }
      if (contentType?.startsWith("text/")) {
        isText = true;
      }
      const content = isText ? response.text() : response.json();

      if (response.ok && response.status !== HttpStatusCodes.NO_CONTENT) {
        return content as Promise<T>;
      }
      const data = await content;
      if (this.isAuthError(response)) {
        this.notifyCredentialsLost();
      }
      const rateLimited = this.maybeNotifyRateLimit(response);
      if (data?.message === "NO_RIGHT_FOR_USER_DATA_READER") {
        import("@/router").then(router => router.default.back());
      }
      return Promise.reject({
        httpHeaders: response.headers,
        httpResponseCode: response.status,
        content: Promise.resolve(data),
        [RATE_LIMITED_ERROR_FLAG]: rateLimited,
      });
    }
    catch {
      return Promise.reject({
        httpHeaders: response.headers,
        httpResponseCode: response.status,
      });
    }
  }

  async downloadFile(urlPath: string): Promise<void> {
    const url = `${config.API_URL}${urlPath}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${this.getAuthorizationToken()}`,
      },
    });
    if (!response.ok) {
      if (this.isAuthError(response)) {
        this.notifyCredentialsLost();
      }
      throw new Error("Erreur au téléchargement !");
    }
    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    const disposition = response.headers.get("Content-Disposition");
    let filename = "fichier.csv";
    if (disposition && disposition.includes("filename=")) {
      const result = disposition.match(/filename="?([^"]+)"?/);
      if (result?.[1]) filename = result[1];
    }
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }

  notifyCredentialsLost(): void {
    if (this.credentialsHandler) {
      this.credentialsHandler.notifyCredentialsLost();
    }
    else {
      this.defaultNotifyCredentialsLost();
    }
    // #470 - Unifie 401 et 403 sur le même chemin de redirection :
    // le listener "disconnected" du router pousse /login avec returnUrl.
    // Sans ça, un 401 laissait l'utilisateur sur sa page avec un état
    // local cassé et ses actions échouaient silencieusement.
    this.dispatchDisconnected();
  }

  convertToFormData(body: Record<string, unknown>): FormData {
    const formData = new FormData();
    if (body) {
      for (const [key, value] of Object.entries(body)) {
        formData.append(key, value as string | Blob);
      }
    }
    return formData;
  }

  getUserPrefLocale(): string {
    const injectedLocale = Fetcher.#dependencies.localeProvider?.getUserPrefLocale();
    if (injectedLocale) {
      return injectedLocale;
    }
    const browserLocale = window.navigator.language.substring(0, 2);
    return (
      localStorage.getItem(LOCAL_STORAGE_LANG)
      || (Object.values(Locales).includes(browserLocale as (typeof Locales)[keyof typeof Locales]) && browserLocale)
      || Locales.FRENCH
    );
  }
}
